import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { emptyStringValues } from '@/utils/objects';
import { toAppWallClockString } from '@/utils/spain-time';

import { CNMC, CNMCStateEnum } from '../types';

import { cnmcQueryKeys } from './query-keys';

export type TCreateCNMCParams = {
  name: string;
  description: string;
  channel: string;
  provider: string;
  start_datetime: Date;
  end_datetime: Date;
  state?: CNMCStateEnum | null;
  recipients?: string | null;
};

export async function createCNMC(body: TCreateCNMCParams): Promise<CNMC> {
  const formattedBody = { ...body };

  formattedBody.start_datetime.setSeconds(0, 0);
  formattedBody.end_datetime.setSeconds(0, 0);

  return mediaManagerClient.post(
    '/api/v1/cnmcs',
    emptyStringValues({
      ...formattedBody,
      start_datetime: toAppWallClockString(formattedBody.start_datetime),
      end_datetime: toAppWallClockString(formattedBody.end_datetime),
    })
  );
}

type MutationFn = typeof createCNMC;

type TUseCreateCNMCParams = {
  config?: MutationConfig<MutationFn>;
};

export function useCreateCNMC({ config = {} }: TUseCreateCNMCParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    mutationFn: createCNMC,
    onError(error) {
      addErrorNotification(
        t('No se ha creado el ID del informe'),
        t('Ha habido un error en el proceso')
      );
      console.error(error);
    },
    onSuccess(data: CNMC, variables: TCreateCNMCParams, context: unknown) {
      addSuccessNotification(
        t('Se ha creado el ID del informe'),
        t('Los datos se han cargado correctamente')
      );
      queryClient.invalidateQueries({ queryKey: cnmcQueryKeys.all });
      config.onSuccess?.(data, variables, context);
    },
  });
}
